import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Offcanvas, Form, Button, Modal } from "react-bootstrap";
import { Input, Switch, Tag, InputNumber } from "antd";
import { showToast } from "../../../../core/data/redux/slices/ToastSlice";
import api from "../../../../core/axios/axiosInstance";
import Select from "react-select";
import { fetchDevices } from "../../../../core/data/redux/slices/AllDeviceSlice";

const SectionHeader = ({ icon, title }) => (
  <div
    style={{
      display: "flex", alignItems: "center", gap: 8,
      margin: "20px 0 12px", borderBottom: "2px solid #f3f4f6", paddingBottom: 8,
    }}
  >
    <span style={{ fontSize: 15 }}>{icon}</span>
    <span style={{ fontWeight: 700, fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em" }}>
      {title}
    </span>
  </div>
);

const EditCompanyOffcanvas = ({ visible, onClose, company }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const [resetPwdOpen, setResetPwdOpen] = useState(false);
  const [resetPwdLoading, setResetPwdLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // ── Assigned Extensions ──────────────────────────────────────────────────
  const buildExtList = (comp) => {
    const saved  = comp?.assignedExtensions || [];
    // Only fall back to PBXDetails when the pool is completely empty (legacy setup
    // where superadmin set primary fields but never used the extensions UI).
    // If the pool already has entries, trust it — an extension absent from the pool
    // has been handed off to an agent and must not be re-added here.
    if (saved.length > 0) return saved;
    const pbxExt = comp?.PBXDetails?.PBX_EXTENSION_NUMBER;
    const pbxTel = comp?.PBXDetails?.PBX_TELEPHONE;
    if (pbxExt && pbxTel) {
      return [{ extensionNumber: pbxExt, PBX_TELEPHONE: pbxTel, assignedDeviceId: null, enabled: comp?.extensionStatus === true || comp?.extensionStatus === "active" }];
    }
    return saved;
  };

  const [extList, setExtList]       = useState(() => buildExtList(company));
  const [newExtNum, setNewExtNum]   = useState("");
  const [newExtTel, setNewExtTel]   = useState("");
  const [newExtDevice, setNewExtDevice] = useState(null);
  const [editingExt, setEditingExt]     = useState(null);
  const [hiddenDIDIds, setHiddenDIDIds] = useState(new Set());
  const [editingDIDId, setEditingDIDId] = useState(null);
  const [didStatuses, setDidStatuses]   = useState({});
  const [newExtChannels, setNewExtChannels] = useState(1);
  const [newExtMonthly, setNewExtMonthly] = useState(0);
  const [newExtPerMin, setNewExtPerMin] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [companyDIDs, setCompanyDIDs] = useState([]);

  const [formData, setFormData] = useState({
    firstname:      company?.firstname || "",
    lastname:       company?.lastname || "",
    role:           company?.role || "companyAdmin",
    companyName:    company?.userInfo?.companyName || "",
    email:          company.email || "",
    countryCode:    company?.phonenumbers?.[0]?.countryCode || "",
    mobile:         company?.phonenumbers?.[0]?.number || "",
    extensionNumber: company?.PBXDetails?.PBX_EXTENSION_NUMBER || "",
    telephone:      company?.PBXDetails?.PBX_TELEPHONE || "",
    accountStatus:  company?.accountStatus === "active",
    emailVerified:  !!company?.emailVerified,
    mobileVerified: !!company?.phonenumbers?.[0]?.isVerified,
  });

  const { devices } = useSelector((state) => state.devices);

  const deviceOptions = (devices || []).map((d) => ({
    value: d.deviceId,
    label: d.deviceName,
  }));

  useEffect(() => { dispatch(fetchDevices()); }, [dispatch]);

  useEffect(() => {
    setExtList(buildExtList(company));
    setNewExtNum("");
    setNewExtTel("");
    setNewExtDevice(null);
    setHiddenDIDIds(new Set());
    setEditingDIDId(null);
    setDidStatuses({});
    if (company?._id) {
      api.get("/superAdmin/didlogic/numbers", { params: { companyAdminId: company._id, limit: 200 } })
        .then((res) => setCompanyDIDs(res.data?.data || []))
        .catch(() => setCompanyDIDs([]));
    } else {
      setCompanyDIDs([]);
    }
  }, [company?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Derive pbxType from the selected device
  const newExtPbxType = newExtDevice
    ? ((devices || []).find((d) => d.deviceId === newExtDevice.value)?.pbxType || "cloud")
    : null;

  const handleAddExtension = () => {
    const ext = newExtNum.trim();
    const tel = newExtTel.trim();
    if (!ext || !tel) {
      dispatch(showToast({ message: "Both extension number and telephone are required", variant: "danger" }));
      return;
    }
    if (!newExtDevice) {
      dispatch(showToast({ message: "Device is required for each extension", variant: "danger" }));
      return;
    }
    const currentStatus = extList.find((e) => e.extensionNumber === editingExt?.extensionNumber)?.enabled ?? true;
    const entry = {
      extensionNumber:  ext,
      PBX_TELEPHONE:    tel,
      assignedDeviceId: newExtDevice.value,
      pbxType:          newExtPbxType,
      enabled:          editingExt ? currentStatus : true,
      channels:         newExtChannels || 1,
      monthlyPrice:     Number(newExtMonthly) || 0,
      perMinuteRate:    Number(newExtPerMin) || 0,
    };
    if (editingExt) {
      setExtList((prev) =>
        prev.map((e) => e.extensionNumber === editingExt.extensionNumber ? entry : e)
      );
      setEditingExt(null);
    } else {
      if (extList.some((e) => e.extensionNumber === ext)) {
        dispatch(showToast({ message: "Extension number already exists", variant: "danger" }));
        return;
      }
      setExtList((prev) => [...prev, entry]);
    }
    setNewExtNum("");
    setNewExtTel("");
    setNewExtDevice(null);
    setNewExtMonthly(0);
    setNewExtPerMin(0);
    setEditingDIDId(null);
  };

  const handleEditDID = (did) => {
    setEditingExt(null);
    setEditingDIDId(did._id);
    setNewExtNum("");
    setNewExtTel(did.number);
    setNewExtDevice(null);
    setNewExtChannels(1);
  };

  const handleEditExtension = (e) => {
    setEditingExt(e);
    setNewExtNum(e.extensionNumber);
    setNewExtTel(e.PBX_TELEPHONE);
    const devMatch = e.assignedDeviceId
      ? (deviceOptions.find((d) => d.value === e.assignedDeviceId) || null)
      : null;
    setNewExtDevice(devMatch);
    setNewExtChannels(e.channels || 1);
    setNewExtMonthly(e.monthlyPrice || 0);
    setNewExtPerMin(e.perMinuteRate || 0);
  };

  const confirmDeleteExtension = () => {
    setExtList((prev) => prev.filter((e) => e.extensionNumber !== deleteTarget));
    setDeleteTarget(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstname.trim()) {
      dispatch(showToast({ message: "First name is required", variant: "danger" }));
      return;
    }
    setLoading(true);
    try {
      const primaryExt = extList[0];
      const extensionStatus = extList.length > 0 && extList.some((ex) => ex.enabled !== false);
      const payload = {
        userId:          company._id,
        firstname:       formData.firstname,
        lastname:        formData.lastname,
        companyName:     formData.companyName,
        extensionNumber: primaryExt?.extensionNumber || "",
        telephone:       primaryExt?.PBX_TELEPHONE || "",
        status:          extensionStatus,
      };
      const response = await api.put("/superAdmin/editCompanyAdminAndAgent", payload);

      // ── Email / role / mobile ────────────────────────────────────
      const origRole   = company?.role || "companyAdmin";
      const origMobile = company?.phonenumbers?.[0]?.number || "";
      const origCC     = company?.phonenumbers?.[0]?.countryCode || "";
      const emailChanged = formData.email !== company.email;
      if (emailChanged || formData.role !== origRole || formData.mobile !== origMobile || formData.countryCode !== origCC) {
        await api.put("/superAdmin/updateUserContact", {
          userId: company._id,
          ...(emailChanged ? { email: formData.email } : {}),
          role:   formData.role,
          mobile: formData.mobile ? { countryCode: formData.countryCode || "", number: formData.mobile } : undefined,
        });
      }

      // ── Account status ───────────────────────────────────────────
      if (formData.accountStatus !== (company?.accountStatus === "active")) {
        const ep = formData.accountStatus ? "/deleteUser/activate" : "/deleteUser/suspend";
        await api.post(ep, { user_id: company._id });
      }

      // ── Verification ─────────────────────────────────────────────
      const origEV = !!company?.emailVerified;
      const origMV = !!company?.phonenumbers?.[0]?.isVerified;
      if (formData.emailVerified !== origEV || formData.mobileVerified !== origMV) {
        await api.put("/superAdmin/updateUserVerification", {
          userId:         company._id,
          isVerified:     formData.emailVerified && formData.mobileVerified,
          emailVerified:  formData.emailVerified,
          mobileVerified: formData.mobileVerified,
        });
      }

      // ── Assigned Extensions ──────────────────────────────────────
      await api.put(`/superAdmin/companies/${company._id}/extensions`, { extensions: extList });

      if (response.data.status === "success") {
        dispatch(showToast({ message: "Company admin updated successfully", variant: "success" }));
        onClose(true);
      }
    } catch (error) {
      dispatch(showToast({ message: error?.response?.data?.error || "Failed to update company admin", variant: "danger" }));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      dispatch(showToast({ message: "Password must be at least 8 characters", variant: "danger" }));
      return;
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])/.test(newPassword)) {
      dispatch(showToast({ message: "Password must contain uppercase, lowercase, number and special character (@$!%*?&#)", variant: "danger" }));
      return;
    }
    if (newPassword !== confirmPassword) {
      dispatch(showToast({ message: "Passwords do not match", variant: "danger" }));
      return;
    }
    setResetPwdLoading(true);
    try {
      await api.post("/superAdmin/resetUserPassword", { userId: company._id, newPassword });
      dispatch(showToast({ message: "Password reset successfully", variant: "success" }));
      setResetPwdOpen(false);
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      dispatch(showToast({ message: err?.response?.data?.error || "Failed to reset password", variant: "danger" }));
    } finally {
      setResetPwdLoading(false);
    }
  };

  return (
    <>
      <Offcanvas show={visible} onHide={onClose} placement="end" style={{ width: "520px" }}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            Edit Company Admin —{" "}
            {company.firstname ? `${company.firstname} ${company.lastname}` : "N/A"}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form onSubmit={handleSubmit}>

            {/* ════════════════ Profile Information ════════════════ */}
            <SectionHeader icon="👤" title="Profile Information" />

            <div className="row mb-3">
              <div className="col-6">
                <Form.Group>
                  <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                  <Input
                    placeholder="First name"
                    value={formData.firstname}
                    onChange={(e) => handleInputChange("firstname", e.target.value)}
                  />
                </Form.Group>
              </div>
              <div className="col-6">
                <Form.Group>
                  <Form.Label>Last Name</Form.Label>
                  <Input
                    placeholder="Last name"
                    value={formData.lastname}
                    onChange={(e) => handleInputChange("lastname", e.target.value)}
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Email <span className="text-danger">*</span></Form.Label>
              <Input
                type="email"
                placeholder="Enter email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={formData.role}
                onChange={(e) => handleInputChange("role", e.target.value)}
              >
                <option value="companyAdmin">Company Admin</option>
                <option value="user">Agent</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Mobile Number</Form.Label>
              <div className="d-flex gap-2">
                <Input
                  placeholder="+971"
                  value={formData.countryCode}
                  onChange={(e) => handleInputChange("countryCode", e.target.value)}
                  style={{ width: 90 }}
                />
                <Input
                  placeholder="5551234567"
                  value={formData.mobile}
                  onChange={(e) => handleInputChange("mobile", e.target.value)}
                  style={{ flex: 1 }}
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Verification</Form.Label>
              <div className="d-flex flex-column gap-2 mt-1">
                <Form.Check
                  type="checkbox"
                  id="co-email-verified"
                  label="Email verified"
                  checked={formData.emailVerified}
                  onChange={(e) => handleInputChange("emailVerified", e.target.checked)}
                />
                <Form.Check
                  type="checkbox"
                  id="co-mobile-verified"
                  label="Mobile verified"
                  checked={formData.mobileVerified}
                  onChange={(e) => handleInputChange("mobileVerified", e.target.checked)}
                />
              </div>
            </Form.Group>

            {/* ════════════════ Company & PBX Settings ════════════ */}
            <SectionHeader icon="🏢" title="Company & PBX Settings" />

            <Form.Group className="mb-3">
              <Form.Label>Company Name</Form.Label>
              <Input
                placeholder="Enter company name"
                value={formData.companyName}
                onChange={(e) => handleInputChange("companyName", e.target.value)}
              />
            </Form.Group>

            {/* ── Assigned Extensions ─────────────────────────────── */}
            <SectionHeader icon="📞" title="Assigned Extensions" />

            {/* Existing list */}
            {(extList.length > 0 || companyDIDs.length > 0) ? (
              <div className="mb-3">
                {extList.map((e) => (
                  <div
                    key={e.extensionNumber}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "7px 12px", marginBottom: 6,
                      background: editingExt?.extensionNumber === e.extensionNumber ? "#eff6ff" : "#f9fafb",
                      border: `1px solid ${editingExt?.extensionNumber === e.extensionNumber ? "#93c5fd" : "#e5e7eb"}`,
                      borderRadius: 6,
                    }}
                  >
                    <span style={{ fontSize: 13 }}>
                      <strong>Ext {e.extensionNumber}</strong>
                      <span style={{ color: "#6b7280", marginLeft: 8 }}>{e.PBX_TELEPHONE}</span>
                      {e.assignedDeviceId && (
                        <span style={{ color: "#9333ea", marginLeft: 6, fontSize: 11 }}>
                          [{deviceOptions.find((d) => d.value === e.assignedDeviceId)?.label || "Device"}]
                        </span>
                      )}
                      <Tag
                        color={(() => { const t = e.assignedDeviceId ? ((devices || []).find((d) => d.deviceId === e.assignedDeviceId)?.pbxType || e.pbxType || "cloud") : (e.pbxType || "cloud"); return t === "local" ? "orange" : "blue"; })()}
                        style={{ fontSize: 10, marginLeft: 6, textTransform: "capitalize" }}
                      >
                        {e.assignedDeviceId ? ((devices || []).find((d) => d.deviceId === e.assignedDeviceId)?.pbxType || e.pbxType || "cloud") : (e.pbxType || "cloud")}
                      </Tag>
                      {Number(e.monthlyPrice) > 0 && (
                        <span style={{ color: "#16a34a", marginLeft: 6, fontSize: 11, fontWeight: 600 }}>
                          ${Number(e.monthlyPrice).toFixed(2)}/mo
                        </span>
                      )}
                      {Number(e.perMinuteRate) > 0 && (
                        <span style={{ color: "#2563eb", marginLeft: 6, fontSize: 11, fontWeight: 600 }}>
                          ${Number(e.perMinuteRate).toFixed(3)}/min
                        </span>
                      )}
                      {(e.channels || 1) > 1 && (
                        <span style={{ color: "#059669", marginLeft: 6, fontSize: 11, fontWeight: 600 }}>
                          {e.channels} ch
                        </span>
                      )}
                    </span>
                    <div className="d-flex gap-2 align-items-center">
                      <Switch
                        size="small"
                        checked={e.enabled !== false}
                        onChange={(checked) =>
                          setExtList((prev) =>
                            prev.map((ex) =>
                              ex.extensionNumber === e.extensionNumber
                                ? { ...ex, enabled: checked }
                                : ex
                            )
                          )
                        }
                        checkedChildren="Active"
                        unCheckedChildren="Off"
                      />
                      <button
                        type="button"
                        onClick={() => handleEditExtension(e)}
                        style={{
                          background: "none", border: "none", cursor: "pointer",
                          color: "#3b82f6", fontSize: 13, padding: "2px 6px",
                        }}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(e.extensionNumber)}
                        style={{
                          background: "none", border: "none", cursor: "pointer",
                          color: "#ef4444", fontSize: 16, lineHeight: 1, padding: "0 4px",
                        }}
                        title="Delete"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
                {companyDIDs.filter(
                  (d) => !hiddenDIDIds.has(d._id) && !extList.some((e) => e.PBX_TELEPHONE === d.number)
                ).map((did) => (
                  <div
                    key={did._id}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "7px 12px", marginBottom: 6,
                      background: editingDIDId === did._id ? "#eff6ff" : "#f9fafb",
                      border: `1px solid ${editingDIDId === did._id ? "#93c5fd" : "#e5e7eb"}`,
                      borderRadius: 6,
                    }}
                  >
                    <span style={{ fontSize: 13 }}>
                      <strong style={{ color: "#9ca3af" }}>—</strong>
                      <span style={{ color: "#6b7280", marginLeft: 8 }}>{did.number}</span>
                      {did.label && <span style={{ color: "#6b7280", marginLeft: 6, fontSize: 11 }}>{did.label}</span>}
                      <Tag color="cyan" style={{ fontSize: 10, marginLeft: 6 }}>DID</Tag>
                    </span>
                    <div className="d-flex gap-2 align-items-center">
                      <Switch
                        size="small"
                        checked={didStatuses[did._id] !== false}
                        onChange={(checked) => setDidStatuses((prev) => ({ ...prev, [did._id]: checked }))}
                        checkedChildren="Active"
                        unCheckedChildren="Off"
                      />
                      <button
                        type="button"
                        onClick={() => handleEditDID(did)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#3b82f6", fontSize: 13, padding: "2px 6px" }}
                        title="Add extension for this number"
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        onClick={() => setHiddenDIDIds((prev) => new Set([...prev, did._id]))}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", fontSize: 16, lineHeight: 1, padding: "0 4px" }}
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-muted small mb-3">No extensions assigned yet.</div>
            )}

            {/* Add / Edit row */}
            <div
              style={{
                background: (editingExt || editingDIDId) ? "#eff6ff" : "#f9fafb",
                border: `1px solid ${(editingExt || editingDIDId) ? "#93c5fd" : "#e5e7eb"}`,
                borderRadius: 8, padding: "10px 12px", marginBottom: 12,
              }}
            >
              <div className="d-flex gap-2 mb-2">
                <Input
                  placeholder="Ext no. (e.g. 1010)"
                  value={newExtNum}
                  onChange={(e) => setNewExtNum(e.target.value)}
                  style={{ flex: 1 }}
                />
                <Input
                  placeholder="Phone no. (e.g. 971585990233)"
                  value={newExtTel}
                  onChange={(e) => !editingDIDId && setNewExtTel(e.target.value)}
                  readOnly={!!editingDIDId}
                  style={{ flex: 1.6, background: editingDIDId ? "#f3f4f6" : undefined }}
                />
              </div>
              <div className="d-flex gap-2 mb-2 align-items-center">
                <div style={{ flex: 1 }}>
                  <Select
                    options={deviceOptions}
                    value={newExtDevice}
                    onChange={setNewExtDevice}
                    placeholder="Select device *"
                    isSearchable
                  />
                </div>
                {newExtPbxType && (
                  <Tag color={newExtPbxType === "local" ? "orange" : "blue"} style={{ fontSize: 11, textTransform: "capitalize", margin: 0 }}>
                    {newExtPbxType}
                  </Tag>
                )}
              </div>
              <div className="d-flex gap-2 mb-2 align-items-center">
                <span style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>Monthly charge ($):</span>
                <InputNumber
                  min={0}
                  step={0.01}
                  value={newExtMonthly}
                  onChange={(v) => setNewExtMonthly(v || 0)}
                  style={{ width: 100 }}
                  size="small"
                />
                <span style={{ fontSize: 11, color: "#9ca3af" }}>Shown to the company admin</span>
              </div>
              <div className="d-flex gap-2 mb-2 align-items-center">
                <span style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>Charge per minute ($):</span>
                <InputNumber
                  min={0}
                  step={0.001}
                  value={newExtPerMin}
                  onChange={(v) => setNewExtPerMin(v || 0)}
                  style={{ width: 100 }}
                  size="small"
                />
                <span style={{ fontSize: 11, color: "#9ca3af" }}>0 = use standard rate by destination</span>
              </div>
              {newExtPbxType && (
                <div className="d-flex gap-2 mb-2 align-items-center">
                  <span style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>Channels:</span>
                  <InputNumber
                    min={1}
                    max={100}
                    value={newExtChannels}
                    onChange={(v) => setNewExtChannels(v || 1)}
                    style={{ width: 80 }}
                    size="small"
                  />
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>Max concurrent agents on this extension</span>
                </div>
              )}
              <div className="d-flex gap-2 justify-content-end">
                <Button
                  type="button"
                  variant={editingExt ? "outline-success" : "outline-primary"}
                  size="sm"
                  onClick={handleAddExtension}
                  style={{ whiteSpace: "nowrap" }}
                >
                  {editingExt ? "Save Extension" : "+ Add Extension"}
                </Button>
                {(editingExt || editingDIDId) && (
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => { setEditingExt(null); setEditingDIDId(null); setNewExtNum(""); setNewExtTel(""); setNewExtDevice(null); setNewExtChannels(1); setNewExtMonthly(0); setNewExtPerMin(0); }}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Account Status</Form.Label>
              <div>
                <Switch
                  checked={formData.accountStatus}
                  onChange={(checked) => handleInputChange("accountStatus", checked)}
                  checkedChildren="Active"
                  unCheckedChildren="Suspended"
                />
              </div>
              <div className="form-text text-muted mt-2">
                When suspended, user cannot login to the system.
              </div>
            </Form.Group>

            {/* ════════════════ Security ═══════════════════════════ */}
            <SectionHeader icon="🔒" title="Security" />

            <div className="mb-4">
              <Button
                variant="outline-warning"
                size="sm"
                type="button"
                onClick={() => { setNewPassword(""); setConfirmPassword(""); setResetPwdOpen(true); }}
              >
                <i className="ti ti-lock me-1" />
                Reset Password
              </Button>
            </div>

            {/* ════════════════ Actions ════════════════════════════ */}
            <div className="d-flex justify-content-end gap-2 pt-2 border-top">
              <Button variant="secondary" type="button" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? "Saving…" : "Save Changes"}
              </Button>
            </div>

          </Form>
        </Offcanvas.Body>
      </Offcanvas>

      {/* ── Delete Extension Confirm Modal ──────────────────────── */}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15 }}>Remove Extension</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to remove <strong>Ext {deleteTarget}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={confirmDeleteExtension}>
            Remove
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Reset Password Modal ─────────────────────────────────── */}
      <Modal show={resetPwdOpen} onHide={() => setResetPwdOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>
            <i className="ti ti-lock me-2 text-warning" />
            Reset Password — {company?.email}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>New Password</Form.Label>
            <Input.Password
              placeholder="Minimum 8 characters"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <div className="form-text text-muted mt-1" style={{ fontSize: 12 }}>
              Min 8 characters with uppercase, lowercase, number and special character (@$!%*?&#)
            </div>
          </Form.Group>
          <Form.Group>
            <Form.Label>Confirm Password</Form.Label>
            <Input.Password
              placeholder="Re-enter new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setResetPwdOpen(false)} disabled={resetPwdLoading}>
            Cancel
          </Button>
          <Button variant="warning" onClick={handleResetPassword} disabled={resetPwdLoading}>
            {resetPwdLoading ? "Resetting…" : "Reset Password"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditCompanyOffcanvas;
